import imageCompression from 'browser-image-compression';
import { ref, uploadBytesResumable, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_WIDTH_OR_HEIGHT = 1920; // Max width or height
const COMPRESSION_QUALITY = 0.8; // 80% quality

// Validate image file
const validateImage = (file) => {
  if (!file) {
    throw new Error('No file selected');
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Please select an image file');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }

  return true;
};

// Upload family photo to Firebase Storage
export const uploadFamilyPhoto = async ({ 
  file, 
  familyId, 
  onProgress = () => {}, 
  onError = () => {}, 
  onSuccess = () => {} 
}) => {
  try {
    // Validate file
    validateImage(file);

    // Compress image if needed
    const compressionOptions = {
      maxSizeMB: MAX_FILE_SIZE / (1024 * 1024),
      maxWidthOrHeight: MAX_WIDTH_OR_HEIGHT,
      useWebWorker: true,
      quality: COMPRESSION_QUALITY,
    };

    const compressedFile = await imageCompression(file, compressionOptions);
    
    // Create storage reference
    const timestamp = Date.now();
    const fileName = `family-photo-${timestamp}.${compressedFile.name.split('.').pop()}`;
    const storageRef = ref(storage, `families/${familyId}/photos/${fileName}`);
    
    // Set metadata
    const metadata = {
      contentType: compressedFile.type,
      customMetadata: {
        uploadedBy: 'family-admin',
        uploadedAt: new Date().toISOString(),
        originalName: file.name,
        compressed: 'true'
      }
    };

    // Upload file
    const uploadTask = uploadBytesResumable(storageRef, compressedFile, metadata);
    
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(progress);
        },
        (error) => {
          const errorMessage = `Upload failed: ${error.message}`;
          console.error('Family photo upload error:', error);
          onError(errorMessage);
          reject(new Error(errorMessage));
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const imageData = {
              url: downloadURL,
              path: uploadTask.snapshot.ref.fullPath,
              fileName: fileName,
              size: uploadTask.snapshot.totalBytes,
              uploadedAt: new Date().toISOString()
            };
            
            onSuccess(downloadURL, imageData);
            resolve(imageData);
          } catch (error) {
            const errorMessage = `Failed to get download URL: ${error.message}`;
            console.error('Error getting download URL:', error);
            onError(errorMessage);
            reject(new Error(errorMessage));
          }
        }
      );
    });
  } catch (error) {
    const errorMessage = error.message || 'Upload failed';
    console.error('Family photo upload error:', error);
    onError(errorMessage);
    throw new Error(errorMessage);
  }
};

// Delete family photo from Firebase Storage
export const deleteFamilyPhoto = async (imagePath) => {
  try {
    if (!imagePath) {
      throw new Error('No image path provided');
    }

    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    
    console.log('Family photo deleted successfully:', imagePath);
    return true;
  } catch (error) {
    console.error('Error deleting family photo:', error);
    throw new Error(`Failed to delete family photo: ${error.message}`);
  }
};
